import { useEffect, useRef, useState } from 'react';

export default function CredentialsSection() {
  const [isVisible, setIsVisible] = useState(false);
  const sectionRef = useRef<HTMLElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setIsVisible(true); },
      { threshold: 0.2 }
    );
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  const clients = [
    'GUCCI',
    'National Theatre of Japan',
    'Porsche',
    'Netflix',
    'Fuji Television',
    'Disney',
  ];

  const collaboratorsInternational = [
    { name: 'Sidi Larbi Cherkaoui', title: 'Choreographer / Belgium' },
    { name: 'Yussef Dayes', title: 'Jazz' },
    { name: 'Damian Jalet', title: 'Choreographer' },
    { name: 'Zakir Hussain', title: 'Tabla / India' },
  ];

  const collaboratorsDomestic = [
    { name: 'Mirai Moriyama', title: 'Actor / Dancer' },
    { name: 'Tamasaburo Bando', title: 'Kabuki' },
    { name: 'Kikunojo Onoe', title: 'Japanese Dance' },
    { name: 'Reijiro Tsumura', title: 'Noh' },
    { name: 'Hatsune Miku × Kodo', title: '' },
    { name: 'Sayuri Ishikawa × Kodo', title: '' },
  ];

  const scope = [
    { title: 'Ceremonial Performances', description: 'Dignified performances for international occasions' },
    { title: 'Stage Direction', description: 'Comprehensive direction fusing tradition and innovation' },
    { title: 'Music Composition', description: 'Music production for film and advertising' },
    { title: 'Cultural Supervision', description: 'Ensuring cultural authenticity of projects' },
    { title: 'Educational Programs', description: 'Workshops and lectures on Japanese music' },
    { title: 'International Collaborations', description: 'Cross-cultural artistic partnerships' },
  ];

  return (
    <section
      id="credentials"
      ref={sectionRef}
      className="py-16 sm:py-24 md:py-32 px-4 sm:px-8 bg-washi"
    >
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div
          className={`grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-12 mb-10 sm:mb-16 transition-all duration-1000 ${
            isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
          }`}
        >
          <div className="relative">
            <h2 className="font-sans text-3xl sm:text-4xl md:text-5xl font-bold text-sumi tracking-wide leading-tight">
              Credentials<br />&amp; Track Record
            </h2>
          </div>
          <div className="md:col-span-2 flex items-center">
            <p className="font-sans text-sm sm:text-lg text-sumi/80 leading-relaxed">
              Trusted by global brands, national institutions, and renowned artists worldwide.
            </p>
          </div>
        </div>

        {/* Three Column Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-5 sm:gap-8 relative">
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-96 h-96 rounded-full bg-indigo/5 -z-10"></div>

          {/* Scope Column */}
          <div
            className={`bg-white/80 backdrop-blur-sm rounded-lg p-6 sm:p-8 transition-all duration-1000 delay-200 ${
              isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-12'
            }`}
          >
            <h3 className="font-sans text-xs font-semibold text-sumi mb-5 tracking-widest uppercase">
              Scope
            </h3>
            <div className="space-y-5">
              {scope.map((item, index) => (
                <div key={index}>
                  <h4 className="font-sans text-sm font-semibold text-sumi mb-1 leading-snug">
                    {item.title}
                  </h4>
                  <p className="font-sans text-xs text-sumi/60 leading-relaxed">
                    {item.description}
                  </p>
                  {index < scope.length - 1 && (
                    <div className="mt-5 border-b border-dashed border-sumi/20"></div>
                  )}
                </div>
              ))}
            </div>
          </div>

          {/* Collaborators Column */}
          <div
            className={`bg-white/80 backdrop-blur-sm rounded-lg p-6 sm:p-8 transition-all duration-1000 delay-400 ${
              isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-12'
            }`}
          >
            <h3 className="font-sans text-xs font-semibold text-sumi mb-5 tracking-widest uppercase">
              Collaborators
            </h3>

            <p className="font-sans text-xs text-sumi/50 tracking-widest uppercase mb-3">International</p>
            <div className="space-y-3 mb-5">
              {collaboratorsInternational.map((c, index) => (
                <div key={index}>
                  <div className="flex items-start gap-3">
                    <div className="w-4 h-4 flex items-center justify-center mt-0.5 shrink-0">
                      <i className="ri-star-fill text-indigo text-xs"></i>
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-sans text-sm text-sumi/90 leading-snug">{c.name}</p>
                      {c.title && <p className="font-sans text-xs text-sumi/50 mt-0.5">{c.title}</p>}
                    </div>
                  </div>
                  {index < collaboratorsInternational.length - 1 && (
                    <div className="mt-3 border-b border-dashed border-sumi/20"></div>
                  )}
                </div>
              ))}
            </div>

            <div className="border-b border-sumi/20 mb-5"></div>

            <p className="font-sans text-xs text-sumi/50 tracking-widest uppercase mb-3">Domestic</p>
            <div className="space-y-3">
              {collaboratorsDomestic.map((c, index) => (
                <div key={index}>
                  <div className="flex items-start gap-3">
                    <div className="w-4 h-4 flex items-center justify-center mt-0.5 shrink-0">
                      <i className="ri-star-fill text-indigo text-xs"></i>
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-sans text-sm text-sumi/90 leading-snug">{c.name}</p>
                      {c.title && <p className="font-sans text-xs text-sumi/50 mt-0.5">{c.title}</p>}
                    </div>
                  </div>
                  {index < collaboratorsDomestic.length - 1 && (
                    <div className="mt-3 border-b border-dashed border-sumi/20"></div>
                  )}
                </div>
              ))}
            </div>
          </div>

          {/* Clients Column */}
          <div
            className={`bg-white/80 backdrop-blur-sm rounded-lg p-6 sm:p-8 transition-all duration-1000 delay-600 ${
              isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-12'
            }`}
          >
            <h3 className="font-sans text-xs font-semibold text-sumi mb-5 tracking-widest uppercase">
              Clients
            </h3>
            <div className="space-y-4">
              {clients.map((client, index) => (
                <div key={index}>
                  <p className="font-sans text-sm text-sumi/80 leading-snug">{client}</p>
                  {index < clients.length - 1 && (
                    <div className="mt-4 border-b border-dashed border-sumi/20"></div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
